<template>
  <div class="member-tasks-section flex flex-col flex-1 min-w-0 min-h-0 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
    <div class="flex items-center gap-2 p-3 rounded-t-xl border-l-4 flex-shrink-0" :class="sectionClass">
      <div class="w-2 h-2 rounded-full flex-shrink-0" :class="dotClass"></div>
      <h3 class="text-sm font-semibold text-gray-900 truncate">{{ title }}</h3>
      <span class="ml-auto text-xs font-medium bg-white px-2 py-0.5 rounded-full shadow-sm flex-shrink-0" :class="textColorClass">{{ tasks.length }} of {{ total || statsCount }}</span>
    </div>
    <div v-if="loading && tasks.length === 0" class="flex-1 py-8 text-center bg-white/50 rounded-b-xl">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 mx-auto" :class="spinnerClass"></div>
      <p class="text-xs text-gray-500 mt-2">Loading...</p>
    </div>
    <div v-else class="flex-1 min-h-0 overflow-y-auto space-y-2 p-3">
      <div v-if="tasks.length === 0" class="text-center py-8 text-gray-400 text-sm">
        No tasks
      </div>
      <div v-for="task in tasks" :key="task.id" @click="$emit('task-click', task.id)"
        class="group task-card bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-all p-4 cursor-pointer border-l-4 overflow-hidden"
        :style="{ borderLeftColor: getPriorityColor(task.priority) }"
        :class="[hoverBorderClass]">
        <div class="flex items-start justify-between mb-2">
          <h4 class="font-semibold text-gray-900 text-sm flex-1" :class="hoverClass">{{ task.title }}</h4>
          <span class="text-xs px-2 py-1 rounded-full text-white ml-2"
            :style="{ backgroundColor: getPriorityColor(task.priority) }">
            {{ task.priority }}
          </span>
        </div>
        <p v-if="task.description" class="text-xs text-gray-600 mb-3 task-card-description">
          {{ task.description }}
        </p>

        <!-- Time Progress Bar (checklist excluded) -->
        <div v-if="task.time_progress_percent != null"
          :class="task.checklist_progress_percent != null ? 'mb-2' : 'mb-3'">
          <div class="flex justify-between text-10px text-gray-500 mb-0.5">
            <span>{{ task.time_progress_percent }}%</span>
            <span v-if="task.estimated_minutes">{{ formatTimeSpent(task.actual_seconds_spent || 0) }} / {{ formatEstimatedDisplay(task.estimated_minutes, task.estimated_time_type) }}</span>
          </div>
          <div class="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div class="task-progress-bar-fill h-full rounded-full transition-all duration-500 ease-out"
              :class="[getProgressBarColorClass(task.time_progress_percent), { 'task-progress-bar-fill--animated': (task.status || '').toLowerCase() === 'in_progress' }, { 'task-progress-bar-fill--static': (task.status || '').toLowerCase() === 'paused' }]"
              :style="{ width: Math.min(100, Math.max(0, task.time_progress_percent)) + '%' }"></div>
          </div>
        </div>
        <!-- Checklist Progress Bar (separate, only when checklist exists) -->
        <div v-if="task.checklist_progress_percent != null" class="mb-3">
          <div class="flex justify-between text-10px text-gray-500 mb-0.5">
            <span>Checklist {{ task.checklist_progress_percent }}%</span>
          </div>
          <div class="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div class="h-full rounded-full transition-all duration-500 ease-out bg-emerald-500"
              :style="{ width: Math.min(100, Math.max(0, task.checklist_progress_percent)) + '%' }"></div>
          </div>
        </div>

        <!-- Creator and Assigned To Section -->
        <div class="mb-3 space-y-1.5 min-w-0">
          <!-- Creator -->
          <div class="flex items-start gap-1.5 text-xs min-w-0">
            <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
            </svg>
            <span class="text-gray-500 flex-shrink-0">Created by</span>
            <div class="w-4 h-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-8px flex-shrink-0">
              {{ getInitials(task.created_by && task.created_by.name) }}
            </div>
            <span class="text-gray-700 break-words min-w-0">{{ (task.created_by && task.created_by.name) || 'Unknown' }}</span>
          </div>

          <!-- Assigned To -->
          <div class="flex items-start gap-1.5 text-xs min-w-0">
            <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
            </svg>
            <span class="text-gray-500 flex-shrink-0">Assigned to</span>
            <div v-if="task.assignees && task.assignees.length > 0" class="flex items-start gap-1 min-w-0 flex-1">
              <div class="flex -space-x-1 flex-shrink-0">
                <div v-for="assignee in task.assignees.slice(0, 3)" :key="assignee.id"
                  class="w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center text-8px border border-white"
                  :title="assignee.name">
                  {{ getInitials(assignee.name) }}
                </div>
                <div v-if="task.assignees.length > 3"
                  class="w-4 h-4 rounded-full bg-gray-400 text-white flex items-center justify-center text-8px border border-white"
                  :title="`${task.assignees.length} assignees`">
                  +{{ task.assignees.length - 3 }}
                </div>
              </div>
              <span class="text-gray-700 break-words ml-0.5 min-w-0">{{ task.assignees.map(a => a.name).join(', ') }}</span>
            </div>
            <span v-else class="text-gray-400 italic">Unassigned</span>
          </div>

          <!-- Viewers -->
          <div v-if="task.viewers && task.viewers.length > 0" class="flex items-start gap-1.5 text-xs min-w-0">
            <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
            </svg>
            <span class="text-gray-500 flex-shrink-0">Viewers</span>
            <div class="flex items-start gap-1 min-w-0 flex-1">
              <div class="flex -space-x-1 flex-shrink-0">
                <div v-for="viewer in task.viewers.slice(0, 3)" :key="viewer.id"
                  class="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-8px border border-white"
                  :title="viewer.name">
                  {{ getInitials(viewer.name) }}
                </div>
                <div v-if="task.viewers.length > 3"
                  class="w-4 h-4 rounded-full bg-gray-400 text-white flex items-center justify-center text-8px border border-white"
                  :title="`${task.viewers.length} viewers`">
                  +{{ task.viewers.length - 3 }}
                </div>
              </div>
              <span class="text-gray-700 break-words ml-0.5 min-w-0">{{ task.viewers.map(v => v.name).join(', ') }}</span>
            </div>
          </div>
        </div>

        <!-- Status badge (all statuses) -->
        <div class="flex items-center gap-1 text-xs mb-1">
          <span :class="[getStatusBadgeClass(task.status), 'inline-flex items-center px-2.5 py-0.5 rounded-full font-medium']">{{ getStatusLabel(task.status) }}</span>
        </div>

        <!-- Completed date (Done tasks) -->
        <div v-if="task.status === 'done' && task.completed_at" class="flex items-center gap-1 text-xs text-green-600">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Completed {{ formatDate(task.completed_at) }}</span>
        </div>

        <!-- Due Date -->
        <div v-else-if="task.due_date" class="flex items-center gap-1 text-xs text-red-500">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span>{{ formatDate(task.due_date) }}</span>
        </div>
      </div>
    </div>
    <button v-if="hasMore" @click="$emit('load-more')" :disabled="loading"
      class="m-3 py-2 px-3 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
      :class="buttonClass">
      {{ loading ? 'Loading...' : 'Load more' }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'TasksStatusSection',
  props: {
    status: { type: String, required: true },
    tasks: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    statsCount: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    title: { type: String, required: true },
    sectionClass: { type: String, default: '' },
    dotClass: { type: String, default: 'bg-gray-500' },
    buttonClass: { type: String, default: '' },
    hoverClass: { type: String, default: 'group-hover:text-gray-600 group-hover:bg-gray-100' },
    hoverBorderClass: { type: String, default: 'hover:border-gray-200' },
    getPriorityColor: { type: Function, required: true },
    formatDate: { type: Function, required: true },
    getInitials: { type: Function, default: (name) => (name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?') },
    formatTimeSpent: { type: Function, default: (s) => (s == null || s < 0 ? '0s' : s < 60 ? s + 's' : Math.floor(s / 60) + 'm') },
    formatMinutes: { type: Function, default: (m) => (m && m >= 0 ? (m < 60 ? m + 'm' : Math.floor(m / 60) + 'h') : '0m') },
    formatEstimatedDisplay: {
      type: Function,
      default: (m, type) => {
        if (!m || m < 0) return '0m';
        if (type === 'days') return (m / 1440 === 1 ? '1 day' : Math.round(m / 1440) + ' days');
        return m < 60 ? m + 'm' : (Math.floor(m / 60) + 'h' + (m % 60 > 0 ? ' ' + (m % 60) + 'm' : ''));
      },
    },
    getProgressBarColorClass: { type: Function, default: () => 'bg-blue-500' },
    getStatusLabel: {
      type: Function,
      default: (status) => {
        const labels = { todo: 'To Do', in_progress: 'In Progress', paused: 'Paused', in_review: 'In Review', done: 'Done' };
        return labels[(status || 'todo').toLowerCase()] || status;
      },
    },
    getStatusBadgeClass: {
      type: Function,
      default: (status) => {
        const s = (status || 'todo').toLowerCase();
        const classes = { todo: 'bg-gray-100 text-gray-700', in_progress: 'bg-blue-100 text-blue-700', paused: 'bg-amber-100 text-amber-700', in_review: 'bg-purple-100 text-purple-700', done: 'bg-green-100 text-green-700' };
        return classes[s] || 'bg-gray-100 text-gray-700';
      },
    },
  },
  computed: {
    textColorClass() {
      if (this.sectionClass.includes('gray')) return 'text-gray-500';
      if (this.sectionClass.includes('blue')) return 'text-blue-700';
      if (this.sectionClass.includes('purple')) return 'text-purple-700';
      if (this.sectionClass.includes('green')) return 'text-green-700';
      return 'text-gray-500';
    },
    spinnerClass() {
      if (this.sectionClass.includes('gray')) return 'border-t-gray-500';
      if (this.sectionClass.includes('blue')) return 'border-t-blue-500';
      if (this.sectionClass.includes('purple')) return 'border-t-purple-500';
      if (this.sectionClass.includes('green')) return 'border-t-green-500';
      return 'border-t-gray-500';
    },
  },
};
</script>

<style scoped>
.task-card-description {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.text-8px {
  font-size: 8px;
}

@keyframes task-progress-flow {
  0% { background-position: 0 0, -100% 0; }
  100% { background-position: 24px 0, 200% 0; }
}

.task-progress-bar-fill {
  background-image:
    repeating-linear-gradient(105deg, transparent 0, transparent 4px, rgba(255, 255, 255, 0.2) 4px, rgba(255, 255, 255, 0.2) 8px),
    linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.35) 45%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0.35) 55%, transparent 100%);
  background-size: 24px 100%, 200% 100%;
  background-position: 0 0, -100% 0;
}

.task-progress-bar-fill--animated {
  animation: task-progress-flow 1.2s linear infinite;
}

.task-progress-bar-fill--static {
  background-image: none !important;
  background-size: unset !important;
  background-position: unset !important;
}
</style>
